import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Flex,
  Input,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { CardPart } from '../chatReducer';
import {
  canTick,
  flagLabel,
  fmtRate,
  initialSelection,
  rateText,
  resultText,
  sectionTicks,
  selectedIds,
  selectionCounts,
  toggle,
  toggleSection,
} from '../pricingView';
import type { NotOnSheetRow, PricingRow, PricingUpdate } from '../types';

const LIST = { margin: '4px 0 8px', paddingLeft: 20 };

const REASON_LABELS: Record<string, string> = {
  not_in_source: "Number isn't in the source",
};

const label = (code: string, labels: Record<string, string>) =>
  labels[code] ?? code.replace(/_/g, ' ');

const occupancyText = (occupancy: string | null) =>
  occupancy === null
    ? 'Any'
    : occupancy.charAt(0) + occupancy.slice(1).toLowerCase().replace(/_/g, '-');

type PricingCardProps = {
  card: CardPart;
  update: PricingUpdate;
  onAct: (
    action: 'confirm' | 'discard',
    opts: { selected?: string[]; reason?: string },
  ) => void;
  busy: boolean;
};

const flagTags = (row: PricingRow | NotOnSheetRow) =>
  [...row.flags, ...row.blocked].map((code) => (
    <Tag key={code} color={row.blocked.includes(code) ? 'red' : 'gold'}>
      {flagLabel(code)}
    </Tag>
  ));

const TextList = ({ title, items }: { title: string; items: string[] }) =>
  items.length === 0 ? null : (
    <div>
      <Typography.Text type="secondary">{title}</Typography.Text>
      <ul style={LIST}>
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );

/**
 * The lender pricing import's card: proposed rows per product with ticks, the
 * current rows the sheet doesn't list, what can't be applied or wasn't
 * imported, then Confirm and Discard. Closed, it shows the result.
 */
const PricingCard = ({ card, update, onAct }: PricingCardProps) => {
  const [sel, setSel] = useState(() => initialSelection(update));
  const [discarding, setDiscarding] = useState(false);
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<'confirm' | 'discard' | null>(null);
  const sending = card.cardState === 'sending';
  const open = card.cardState === 'open';
  const offers = (action: string) => card.actions.includes(action);
  const ids = selectedIds(update, sel);
  const counts = selectionCounts(update, sel);
  const total = counts.add + counts.change + counts.end;
  const locked = !open;
  const { summary } = update;

  const rowColumns = [
    {
      title: 'Occupancy',
      dataIndex: 'occupancy',
      render: (v: string | null) => occupancyText(v),
    },
    { title: 'LVR', dataIndex: 'lvr' },
    { title: 'Loan', dataIndex: 'loan' },
    {
      title: 'Rate',
      key: 'rate',
      render: (_: unknown, row: PricingRow) => rateText(row),
    },
    {
      title: '',
      key: 'flags',
      render: (_: unknown, row: PricingRow) => flagTags(row),
    },
  ];

  return (
    <Card size="small" title={card.title}>
      <Flex vertical gap={8}>
        <Typography.Text>
          {`KALI read ${summary.pages} pages and ${summary.rates} rates in ${summary.seconds} s`}
        </Typography.Text>
        {update.note && <Alert type="info" showIcon message={update.note} />}

        {update.sections.map((section, index) => {
          const ticks = sectionTicks(update, sel, index);
          return (
            <div key={`${section.productId}-${index}`}>
              <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
                <Checkbox
                  aria-label={`Tick all of ${section.title}`}
                  checked={ticks.all}
                  indeterminate={ticks.some && !ticks.all}
                  disabled={locked || !section.rows.some(canTick)}
                  onChange={() => setSel(toggleSection(update, sel, index))}
                />
                <Typography.Text strong>
                  {`${section.title}${section.page ? ` · p.${section.page}` : ''}`}
                </Typography.Text>
                <Typography.Text type="secondary">
                  {section.productName}
                </Typography.Text>
              </Flex>
              <Table<PricingRow>
                size="small"
                pagination={false}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                columns={rowColumns}
                dataSource={section.rows}
                rowSelection={{
                  selectedRowKeys: section.rows
                    .filter((r) => sel.has(r.id))
                    .map((r) => r.id),
                  getCheckboxProps: (row) => ({
                    disabled: locked || !canTick(row),
                  }),
                  onSelect: (row) => setSel(toggle(sel, row.id)),
                  hideSelectAll: true,
                }}
              />
            </div>
          );
        })}

        {update.notOnSheet.length > 0 && (
          <div>
            <Typography.Text strong>Not on this sheet</Typography.Text>{' '}
            <Typography.Text type="secondary">
              Ticking one ends it.
            </Typography.Text>
            <Table<NotOnSheetRow>
              size="small"
              pagination={false}
              rowKey="id"
              scroll={{ x: 'max-content' }}
              columns={[
                { title: 'Product', dataIndex: 'productName' },
                {
                  title: 'Occupancy',
                  dataIndex: 'occupancy',
                  render: (v: string | null) => occupancyText(v),
                },
                { title: 'LVR', dataIndex: 'lvr' },
                { title: 'Loan', dataIndex: 'loan' },
                {
                  title: 'Current rate',
                  dataIndex: 'oldRate',
                  render: (v: string | null) =>
                    v === null ? '' : `${fmtRate(v)}%`,
                },
                {
                  title: '',
                  key: 'flags',
                  render: (_: unknown, row: NotOnSheetRow) => flagTags(row),
                },
              ]}
              dataSource={update.notOnSheet}
              rowSelection={{
                selectedRowKeys: update.notOnSheet
                  .filter((r) => sel.has(r.id))
                  .map((r) => r.id),
                getCheckboxProps: (row) => ({
                  disabled: locked || !canTick(row),
                }),
                onSelect: (row) => setSel(toggle(sel, row.id)),
                hideSelectAll: true,
              }}
            />
          </div>
        )}

        {update.cannotApply.length > 0 && (
          <div>
            <Typography.Text type="secondary">Can't be applied</Typography.Text>
            <ul style={LIST}>
              {update.cannotApply.map((item, i) => (
                <li key={i}>
                  {`${item.section}: "${item.printed}" (${item.reasons
                    .map((r) => label(r, REASON_LABELS))
                    .join(', ')})`}
                </li>
              ))}
            </ul>
          </div>
        )}
        <TextList title="Not imported" items={update.notImported} />

        {update.effectiveFrom && (
          <Typography.Text type="secondary">
            {`Effective ${update.effectiveFrom}`}
          </Typography.Text>
        )}

        {offers('confirm') && !card.pricingResult && (
          <Flex gap={8} wrap>
            <Button
              type="primary"
              disabled={locked || total === 0}
              loading={sending && pending === 'confirm'}
              onClick={() => {
                setPending('confirm');
                onAct('confirm', { selected: ids });
              }}
            >
              {`Apply ${total} change${total === 1 ? '' : 's'}`}
            </Button>
            {offers('discard') && (
              <Button disabled={locked} onClick={() => setDiscarding(true)}>
                Discard
              </Button>
            )}
          </Flex>
        )}

        {discarding && open && (
          <Flex gap={8}>
            <Input.TextArea
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={500}
              aria-label="Reason for discarding"
              placeholder="Why are you discarding it? (optional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              danger
              loading={sending && pending === 'discard'}
              onClick={() => {
                setPending('discard');
                onAct('discard', { reason: reason.trim() || undefined });
              }}
            >
              Confirm discard
            </Button>
          </Flex>
        )}

        {card.pricingResult && (
          <Alert
            type={card.pricingResult.status === 'applied' ? 'success' : 'info'}
            showIcon
            message={resultText(card.pricingResult)}
            description={
              card.pricingResult.status === 'applied' &&
              card.pricingResult.lenderId ? (
                <Link to={`/settings/lenders/${card.pricingResult.lenderId}`}>
                  Open the lender's pricing
                </Link>
              ) : undefined
            }
          />
        )}
        {card.cardError && (
          <Alert type="error" showIcon message={card.cardError} />
        )}
      </Flex>
    </Card>
  );
};

export default PricingCard;
